import { useState } from "react";
import { Link } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import { Heart, MessageCircle, Share2, Bookmark, MoreHorizontal, Flag, Sparkles, Pin, Trash2 } from "lucide-react";
import { authClient, useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { sfx } from "@/lib/sounds";

function highlight(text) {
  if (!text) return null;
  return text.split(/(\s+)/).map((part, i) => {
    const key = `${i}-${part.slice(0, 8)}`;
    if (part.startsWith("#")) return <span key={key} className="text-[#FFBF00] font-medium">{part}</span>;
    if (part.startsWith("@")) return <span key={key} className="text-[#FF8C00] font-medium">{part}</span>;
    return <span key={key}>{part}</span>;
  });
}

function timeAgo(ts) {
  try { return formatDistanceToNow(new Date(ts), { addSuffix: true }); } catch { return ""; }
}

function PollBlock({ post, onVote }) {
  const poll = post.poll;
  if (!poll) return null;
  const total = Object.values(post.poll_votes || {}).reduce((a, b) => a + Number(b || 0), 0);
  const voted = typeof post.voted_option === "number";
  return (
    <div className="mt-3 space-y-2">
      {poll.question && <div className="text-sm text-white/80 font-medium">{poll.question}</div>}
      {poll.options.map((opt, i) => {
        const votes = Number((post.poll_votes || {})[String(i)] || 0);
        const pct = total > 0 ? Math.round((votes / total) * 100) : 0;
        const isMine = post.voted_option === i;
        return (
          <button key={`opt-${i}-${opt}`}
                  onClick={() => onVote?.(i)}
                  disabled={voted}
                  className={`relative w-full text-left px-3 py-2 rounded-lg border ${isMine ? "border-[#FFBF00]/60" : "border-white/10"} bg-black/40 overflow-hidden`}>
            <div className="absolute inset-0" style={{ width: `${pct}%`, background: "linear-gradient(90deg, rgba(255,191,0,0.18), rgba(255,140,0,0.08))" }} />
            <div className="relative flex items-center justify-between text-sm">
              <span className="text-white/90">{opt}</span>
              {voted && <span className="text-[10px] font-mono text-white/60">{pct}% · {votes}</span>}
            </div>
          </button>
        );
      })}
      <div className="text-[10px] text-white/40">{total} vote{total === 1 ? "" : "s"}{voted ? " · you voted" : ""}</div>
    </div>
  );
}

export default function PostCard({ post, onDelete, onUpdate }) {
  const { user } = useAuth();
  const [p, setP] = useState(post);
  const [showMenu, setShowMenu] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState([]);
  const [commentBody, setCommentBody] = useState("");

  const toggleLike = async () => {
    if (!user) return toast.error("Sign in to like");
    try {
      const { data } = await authClient.post(`/feed/${p.id}/like`);
      const next = { ...p, liked: data.liked, likes_count: data.count };
      setP(next); onUpdate?.(next);
      data.liked ? sfx.pling() : sfx.click();
    } catch (err) { sfx.error(); console.warn("like failed", err?.message); }
  };
  const toggleBookmark = async () => {
    if (!user) return toast.error("Sign in to bookmark");
    try {
      const { data } = await authClient.post(`/feed/${p.id}/bookmark`);
      const next = { ...p, bookmarked: data.bookmarked, bookmarks_count: data.count };
      setP(next); onUpdate?.(next);
      sfx.click();
    } catch (err) { sfx.error(); console.warn("bookmark failed", err?.message); }
  };
  const doShare = async () => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/community#${p.id}`);
      toast.success("Link copied");
    } catch (err) { console.warn("clipboard failed", err?.message); }
    try { await authClient.post(`/feed/${p.id}/share`); }
    catch (err) { console.warn("share bump failed", err?.message); }
    setP({ ...p, shares_count: (p.shares_count || 0) + 1 });
  };
  const vote = async (idx) => {
    if (!user) return toast.error("Sign in to vote");
    try {
      const { data } = await authClient.post(`/feed/${p.id}/vote/${idx}`);
      setP({ ...p, poll_votes: data.votes, voted_option: idx });
      sfx.click();
    } catch (err) { sfx.error(); console.warn("vote failed", err?.message); }
  };
  const doReport = async () => {
    const reason = window.prompt("Report reason?");
    if (!reason) return;
    try { await authClient.post(`/feed/${p.id}/report`, { reason }); toast.success("Reported"); }
    catch (err) { console.warn("report failed", err?.message); toast.error("Failed"); }
    setShowMenu(false);
  };
  const doDelete = async () => {
    if (!window.confirm("Delete this post?")) return;
    try { await authClient.delete(`/feed/${p.id}`); onDelete?.(p.id); toast.success("Deleted"); sfx.no(); }
    catch (err) { console.warn("delete failed", err?.message); toast.error("Failed"); }
  };

  const loadComments = async () => {
    setShowComments((s) => !s);
    if (!showComments && comments.length === 0) {
      try {
        const { data } = await authClient.get(`/feed/${p.id}/comments`);
        setComments(data);
      } catch (err) { console.warn("load comments failed", err?.message); }
    }
  };
  const submitComment = async (e) => {
    e.preventDefault();
    if (!commentBody.trim() || !user) return;
    try {
      const { data } = await authClient.post(`/feed/${p.id}/comments`, { body: commentBody });
      setComments((c) => [...c, data]);
      setCommentBody("");
      setP({ ...p, comments_count: (p.comments_count || 0) + 1 });
      sfx.click();
    } catch (err) { sfx.error(); console.warn("submit comment failed", err?.message); }
  };

  const isOwner = user && user.id === p.author_id;

  return (
    <article className="rounded-2xl glass p-5" data-testid={`post-${p.id}`} id={p.id}>
      {p.pinned && <div className="flex items-center gap-1.5 mb-2 text-[10px] uppercase tracking-widest text-[#FFBF00]"><Pin size={10} /> Pinned</div>}
      <header className="flex items-start gap-3">
        <Link to={`/users/${p.author_id}`} className="shrink-0">
          <img src={p.author_avatar || `https://api.dicebear.com/9.x/pixel-art-neutral/svg?seed=${p.author_id}`}
               alt="" className="w-11 h-11 rounded-lg border border-white/10 object-cover" />
        </Link>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <Link to={`/users/${p.author_id}`} className="font-semibold hover:text-[#FFBF00] line-clamp-1">
              {p.author_name}
            </Link>
            {p.author_is_creator && <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widest text-[#FFBF00]"><Sparkles size={9} /> Creator</span>}
          </div>
          <div className="text-[10px] text-white/40 uppercase tracking-widest">{timeAgo(p.created_at)} · {p.kind.replace("_", " ")}</div>
        </div>
        <div className="relative">
          <button onClick={() => setShowMenu((s) => !s)} className="p-1.5 rounded-full hover:bg-white/5 text-white/50" data-testid={`post-menu-${p.id}`}>
            <MoreHorizontal size={16} />
          </button>
          {showMenu && (
            <div className="absolute right-0 top-8 rounded-lg glass border border-white/10 py-1 min-w-[140px] z-20">
              <button onClick={doReport} className="w-full px-3 py-2 text-left text-[11px] uppercase tracking-widest hover:bg-white/5 flex items-center gap-2"><Flag size={11} /> Report</button>
              {(isOwner || user?.role === "admin") && (
                <button onClick={doDelete} className="w-full px-3 py-2 text-left text-[11px] uppercase tracking-widest text-red-400 hover:bg-white/5 flex items-center gap-2"><Trash2 size={11} /> Delete</button>
              )}
            </div>
          )}
        </div>
      </header>

      {p.body && <div className="mt-3 text-sm text-white/85 whitespace-pre-wrap break-words">{highlight(p.body)}</div>}

      {p.images && p.images.length > 0 && (
        <div className={`mt-3 grid gap-1.5 rounded-xl overflow-hidden ${p.images.length === 1 ? "grid-cols-1" : "grid-cols-2"}`}>
          {p.images.map((src, i) => (
            <img key={`img-${i}-${(src || "").slice(-16)}`} src={src} alt="" className="w-full max-h-[440px] object-cover rounded-md border border-white/5" />
          ))}
        </div>
      )}

      {p.video_url && (
        <a href={p.video_url} target="_blank" rel="noreferrer"
           className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/10 hover:border-[#FFBF00]/40 text-[11px] uppercase tracking-widest text-white/70">
          Watch video ↗
        </a>
      )}

      {p.build_meta && (
        <Link to="/builds" className="mt-3 flex items-center gap-3 rounded-lg border border-white/10 p-2 hover:border-[#FFBF00]/40">
          {p.build_meta.thumbnail && <img src={p.build_meta.thumbnail} alt="" className="w-16 h-16 rounded object-cover" />}
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#FFBF00]">Build</div>
            <div className="text-sm font-semibold">{p.build_meta.title}</div>
          </div>
        </Link>
      )}

      {p.kind === "poll" && <PollBlock post={p} onVote={vote} />}

      {p.tags?.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {p.tags.slice(0, 8).map((t) => (
            <Link key={t} to={`/community?tag=${encodeURIComponent(t)}`}
                  className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border border-white/10 hover:border-[#FFBF00]/40 text-white/60 hover:text-[#FFBF00]">
              #{t}
            </Link>
          ))}
        </div>
      )}

      <footer className="mt-4 flex items-center gap-1 text-xs text-white/60 border-t border-white/5 pt-3">
        <button onClick={toggleLike} data-testid={`post-like-${p.id}`}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md hover:bg-white/5 ${p.liked ? "text-[#FF3B5F]" : ""}`}>
          <Heart size={14} fill={p.liked ? "currentColor" : "none"} /> {p.likes_count || 0}
        </button>
        <button onClick={loadComments} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md hover:bg-white/5">
          <MessageCircle size={14} /> {p.comments_count || 0}
        </button>
        <button onClick={doShare} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md hover:bg-white/5">
          <Share2 size={14} /> {p.shares_count || 0}
        </button>
        <button onClick={toggleBookmark}
                className={`ml-auto inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md hover:bg-white/5 ${p.bookmarked ? "text-[#FFBF00]" : ""}`}>
          <Bookmark size={14} fill={p.bookmarked ? "currentColor" : "none"} />
        </button>
      </footer>

      {showComments && (
        <div className="mt-3 space-y-3 border-t border-white/5 pt-3">
          {comments.map((c) => (
            <div key={c.id} className="flex gap-2">
              <img src={c.author_avatar} alt="" className="w-7 h-7 rounded-full border border-white/10" />
              <div className="flex-1 rounded-lg bg-white/[0.03] px-3 py-2">
                <div className="text-[11px] font-semibold">{c.author_name}</div>
                <div className="text-sm text-white/80 whitespace-pre-wrap break-words">{highlight(c.body)}</div>
                <div className="text-[9px] uppercase tracking-widest text-white/30 mt-1">{timeAgo(c.created_at)}</div>
              </div>
            </div>
          ))}
          {user && (
            <form onSubmit={submitComment} className="flex gap-2">
              <input value={commentBody} onChange={(e) => setCommentBody(e.target.value)}
                     placeholder="Reply..." maxLength={1200}
                     className="flex-1 bg-black/50 border border-white/10 rounded-full px-4 py-2 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#FFBF00]/40" />
              <button type="submit" className="px-4 py-2 rounded-full bg-[#FFBF00] hover:bg-[#FF8C00] text-black text-[10px] uppercase tracking-widest font-semibold">Send</button>
            </form>
          )}
        </div>
      )}
    </article>
  );
}
